// Shared member-classification helper (Feb 2026).
//
// Single source of truth for badge label + colour across:
//   - MemberManagement.js
//   - AdminPaymentVerification.js
//   - AdminPaymentsHub.js
//   - PaymentDetailDialog.jsx
//   - any future page that displays a parent's member status
//
// Branch ladder (first match wins for the PRIMARY label + colour):
//   1. Section Member         → emerald   (plays for a Section team)
//   2. Verified SCC Member    → blue      (SCC club member, not Section)
//   3. Pending Verification   → amber     (claimed SCC, awaiting admin review)
//   4. Digital Member         → teal      (is_digital_member only, no SCC/Section)
//   5. Academy only           → slate     (academy_parent/coach/player, no membership)
//   6. Non-Member             → red       (Section-track user who never became a member)
//
// Secondary chips appended to .label (bullet-separated): Academy Player /
// Academy Parent / Academy Coach / Merchant / team-name (Section only).
//
// Secondary DM tag: when primary is Section/SCC/Pending AND parent has a
// valid (non-expired) DM, returns `secondaryTags: [{label: "DM", colorClass}]`
// for the consumer to render as a small inline pill next to the primary badge.

const isDmActive = (member) => {
  if (!member?.is_digital_member) return false;
  const exp = member.digital_membership_expires_at;
  if (!exp) return true;
  try {
    const d = new Date(exp);
    if (Number.isNaN(d.getTime())) return true;
    return d.getTime() >= Date.now();
  } catch {
    return true;
  }
};

// A membership number the system auto-generated at approval (SCC- + 8 hex),
// NOT a real/verified SCC club number. Used to warn admins not to trust it.
export const isPlaceholderMembershipNumber = (num) =>
  !!num && /^SCC-[0-9A-Fa-f]{8}$/.test(String(num).trim());

const fmtDate = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return null;
  }
};

// Digital-membership status for display: number, start (signup), expiry and a
// live validity badge (active / expiring soon / expired / no expiry on file).
export const getDigitalMembershipStatus = (member) => {
  if (!member?.is_digital_member) return { isDM: false };
  const number = member.digital_membership_number || null;
  const start = fmtDate(member.digital_membership_paid_date);
  const expRaw = member.digital_membership_expires_at;
  const expiry = fmtDate(expRaw);

  let status = "no_expiry";
  let daysLeft = null;
  if (expRaw) {
    const d = new Date(expRaw);
    if (!Number.isNaN(d.getTime())) {
      daysLeft = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) status = "expired";
      else if (daysLeft <= 30) status = "expiring_soon";
      else status = "active";
    }
  }

  const badge = {
    active: { label: expiry ? `Active until ${expiry}` : "Active", colorClass: "bg-teal-500/20 text-teal-300" },
    expiring_soon: { label: `Expiring soon (${expiry})`, colorClass: "bg-amber-500/20 text-amber-300" },
    expired: { label: expiry ? `Expired ${expiry}` : "Expired", colorClass: "bg-red-500/20 text-red-400" },
    no_expiry: { label: "No expiry on file", colorClass: "bg-orange-500/20 text-orange-300" },
  }[status];

  return { isDM: true, number, start, expiry, status, daysLeft, badgeLabel: badge.label, badgeColor: badge.colorClass };
};

export const getMemberCategory = (member) => {
  if (!member) return "non_member";
  const roles = member.roles || [];
  const isAcademyOnly =
    roles.includes("academy_parent") ||
    roles.includes("academy_coach") ||
    roles.includes("academy_player");
  if (member.member_type === "section") return "section";
  if (member.scc_member_verified === true) return "verified_scc";
  if (member.is_scc_member === true) return "pending";
  if (isDmActive(member)) return "digital_member";
  if (isAcademyOnly) return "academy_only";
  return "non_member";
};

export const getMemberClassification = (member) => {
  if (!member) {
    return { label: "Non-Member", colorClass: "bg-red-500/20 text-red-400", secondaryTags: [] };
  }
  const roles = member.roles || [];
  const isAcademyParent = roles.includes("academy_parent");
  const isAcademyCoach = roles.includes("academy_coach");
  const isAcademyPlayer = roles.includes("academy_player");
  const isAcademyOnly = isAcademyParent || isAcademyCoach || isAcademyPlayer;
  const isMerchant = member.role === "merchant" || roles.includes("merchant");
  const isSection = member.member_type === "section";
  const isSccVerified = member.scc_member_verified === true;
  const isSccClaimed = member.is_scc_member === true;
  const dmActive = isDmActive(member);
  const teamName = member.primary_team_name || member.primary_team || null;

  const classification = [];
  let colorClass = "bg-slate-800/50 text-slate-300";
  let primaryKey = "non_member";

  if (isSection) {
    classification.push("Section Member");
    colorClass = "bg-emerald-500/20 text-emerald-400";
    primaryKey = "section";
  } else if (isSccVerified) {
    classification.push("Verified SCC Member");
    colorClass = "bg-blue-500/20 text-blue-400";
    primaryKey = "verified_scc";
  } else if (isSccClaimed) {
    classification.push("Pending Verification");
    colorClass = "bg-amber-500/20 text-amber-400";
    primaryKey = "pending";
  } else if (dmActive) {
    classification.push("Digital Member");
    colorClass = "bg-teal-500/20 text-teal-400";
    primaryKey = "digital_member";
  } else if (isAcademyOnly) {
    classification.push("Academy only");
    colorClass = "bg-slate-700/40 text-slate-300";
    primaryKey = "academy_only";
  } else {
    classification.push("Non-Member");
    colorClass = "bg-red-500/20 text-red-400";
    primaryKey = "non_member";
  }

  // Append team for Section
  if (isSection && teamName) classification.push(teamName);

  // Bullet-separated secondary chips (legacy behaviour preserved)
  if (isAcademyPlayer) classification.push("Academy Player");
  if (isAcademyParent) classification.push("Academy Parent");
  if (isAcademyCoach) classification.push("Academy Coach");
  if (isMerchant) classification.push("Merchant");

  // Secondary DM pill when primary is something else but DM is still active.
  // (Digital Member primary tier doesn't get a duplicate DM pill.)
  const secondaryTags = [];
  if (dmActive && primaryKey !== "digital_member") {
    secondaryTags.push({
      label: "DM",
      colorClass: "bg-teal-500/30 text-teal-200",
    });
  }

  return {
    label: classification.join(" • "),
    colorClass,
    primaryKey,
    secondaryTags,
  };
};
